/**
 * Navbar Component
 * Top navigation bar with institution name, academic year selector, notifications, and user menu
 */
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import {
  BookOpen, ChevronDown, LogOut, User, Menu,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import TeacherProfileModal from '../profile/TeacherProfileModal';
import NotificationBell from '../notifications/NotificationBell';
import systemConfigService from '../../services/config/systemConfigService';

export default function Navbar({ onMenuToggle }) {
  const { userProfile, signOut } = useAuth();
  const { currentAcademicYear } = useAcademicYear();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [notifications] = useState([]); // Placeholder for notification context
  const userMenuRef = useRef(null);
  const [instituteInfo, setInstituteInfo] = useState({ nameEn: 'Loading...', logoLink: null });
  
  // Status Indicator States
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isReadQuotaExceeded, setIsReadQuotaExceeded] = useState(false);
  const [isWriteQuotaExceeded, setIsWriteQuotaExceeded] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    const handleReadQuotaError = () => setIsReadQuotaExceeded(true);
    const handleWriteQuotaError = () => setIsWriteQuotaExceeded(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('firebase-read-quota-exceeded', handleReadQuotaError);
    window.addEventListener('firebase-write-quota-exceeded', handleWriteQuotaError);
    
    // Also support generic quota error for backward compatibility
    const handleGenericQuotaError = () => {
      setIsReadQuotaExceeded(true);
      setIsWriteQuotaExceeded(true);
    };
    window.addEventListener('firebase-quota-exceeded', handleGenericQuotaError);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('firebase-read-quota-exceeded', handleReadQuotaError);
      window.removeEventListener('firebase-write-quota-exceeded', handleWriteQuotaError);
      window.removeEventListener('firebase-quota-exceeded', handleGenericQuotaError);
    };
  }, []);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const conf = await systemConfigService.getConfig();
        setInstituteInfo({
          nameEn: conf?.instituteInfo?.nameEn || 'Madrasah Al-Iman',
          logoLink: conf?.instituteInfo?.logoLink || null
        });
      } catch (error) {
        console.error('Failed to fetch institute info:', error);
        setInstituteInfo({ nameEn: 'Madrasah Al-Iman', logoLink: null });
        if (error.message?.toLowerCase().includes('quota')) {
          window.dispatchEvent(new CustomEvent('firebase-read-quota-exceeded'));
        }
      }
    };
    fetchConfig();
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const roleLabels = {
    superAdmin: 'Super Admin',
    admin: 'Admin',
    teacher: 'Teacher',
  };

  const roleBadgeColors = {
    superAdmin: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    admin: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    teacher: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-slate-900/95 backdrop-blur-xl border-b border-white/5">
      <div className="h-full flex items-center justify-between px-4 lg:px-6">
        {/* Left: Logo + Institution */}
        <div className="flex items-center gap-3">
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 transition-all"
          >
            <Menu size={20} />
          </button>

          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0">
              {instituteInfo.logoLink ? (
                <img src={instituteInfo.logoLink} alt="Logo" className="w-full h-full object-contain rounded-xl" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center shadow-lg shadow-teal-500/20 rounded-xl">
                  <BookOpen size={18} className="text-white" />
                </div>
              )}
            </div>
            <div className="hidden sm:block">
              <h1 className="text-sm font-bold text-white leading-tight">
                Exam Management System
              </h1>
              <p className="text-[10px] text-slate-500 leading-tight tracking-wide">
                {instituteInfo.nameEn}
              </p>
            </div>
          </Link>
        </div>

        {/* Center: Academic Year & Status Indicators */}
        <div className="flex items-center gap-2 md:gap-4">
          {currentAcademicYear && (
            <div className="hidden md:flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-800/60 border border-white/5">
              <span className="text-xs text-slate-500">Academic Year</span>
              <span className="text-sm font-semibold text-teal-400">{currentAcademicYear}</span>
            </div>
          )}

          {/* Status Indicators */}
          <div className="flex items-center gap-2 md:gap-3 px-2.5 py-1.5 rounded-full bg-slate-800/60 border border-white/5">
            {/* Database Connection Indicator */}
            <div className="flex items-center gap-1.5 group relative" title={isOnline ? 'Database Connected' : 'Database Disconnected'}>
              <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px_rgba(0,0,0,0.5)] ${isOnline ? 'bg-green-500 shadow-green-500/50' : 'bg-red-500 shadow-red-500/50 animate-pulse'}`} />
            </div>

            <div className="w-px h-3 bg-white/10" />

            {/* Read Quota Indicator */}
            <div className="flex items-center gap-1.5 group relative" title={!isReadQuotaExceeded ? 'Read Limit Available' : 'Read Limit Exceeded'}>
              <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px_rgba(0,0,0,0.5)] ${!isReadQuotaExceeded ? 'bg-green-500 shadow-green-500/50' : 'bg-red-500 shadow-red-500/50 animate-pulse'}`} />
            </div>
            
            <div className="w-px h-3 bg-white/10" />

            {/* Write Quota Indicator */}
            <div className="flex items-center gap-1.5 group relative" title={!isWriteQuotaExceeded ? 'Write Limit Available' : 'Write Limit Exceeded'}>
              <div className={`w-2.5 h-2.5 rounded-full shadow-[0_0_8px_rgba(0,0,0,0.5)] ${!isWriteQuotaExceeded ? 'bg-green-500 shadow-green-500/50' : 'bg-red-500 shadow-red-500/50 animate-pulse'}`} />
            </div>
          </div>
        </div>

        {/* Right: Notifications + User */}
        <div className="flex items-center gap-2">
          {/* Notification Bell */}
          <NotificationBell />

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2.5 pl-3 pr-2 py-1.5 rounded-xl hover:bg-white/5 transition-all"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-600 to-violet-600 flex items-center justify-center text-white text-xs font-bold shadow-md overflow-hidden">
                {userProfile?.coreProfile?.photoUrl ? (
                  <img src={userProfile.coreProfile.photoUrl} alt="User" className="w-full h-full object-cover" />
                ) : (
                  userProfile?.displayName?.charAt(0)?.toUpperCase() || 'U'
                )}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-white leading-tight truncate max-w-[120px]">
                  {userProfile?.displayName || 'User'}
                </p>
                <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full border ${roleBadgeColors[userProfile?.role] || roleBadgeColors.teacher}`}>
                  {roleLabels[userProfile?.role] || 'User'}
                </span>
              </div>
              <ChevronDown size={14} className={`text-slate-500 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown */}
            {showUserMenu && (
              <div className="absolute right-0 top-full mt-2 w-56 py-2 bg-slate-800/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl shadow-black/40 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="px-4 py-3 border-b border-white/5">
                  <p className="text-sm font-medium text-white">{userProfile?.displayName}</p>
                  <p className="text-xs text-slate-500 truncate">{userProfile?.email}</p>
                </div>
                <div className="py-1">
                  <button 
                    onClick={() => {
                      setShowUserMenu(false);
                      setShowProfileModal(true);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-all"
                  >
                    <User size={16} />
                    Profile
                  </button>
                </div>
                <div className="border-t border-white/5 pt-1">
                  <button
                    onClick={signOut}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all"
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
      
      {/* Profile Modal */}
      <TeacherProfileModal 
        isOpen={showProfileModal} 
        onClose={() => setShowProfileModal(false)}
        profile={userProfile?.coreProfile || userProfile}
        userRole={userProfile?.role}
      />
    </>
  );
}
